import React from 'react';
import { Autocomplete, Box, Chip, FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import { CATEGORIES, gateGroup, gateLabel } from '../../helpers/parkingEvents';

export const DEFAULT_FILTER = Object.freeze({
    cats: null, // null = every category
    problemsOnly: false,
    noise: false,
    context: true,
    types: [],
    gates: [],
});

const count = (items, pred) => items.reduce((n, i) => n + (pred(i) ? 1 : 0), 0);

/**
 * Category chips (with counts), problems only / noise / context switches, event type and gate pickers
 * @param items all items before filtering (the counts and the picker options come from them)
 */
export default function EventFilters({
    items: all,
    filter,
    onChange,
    showContext = false,
    pickers = true,
}) {
    const set = (patch) => onChange({ ...filter, ...patch });
    // counts / options only from what can be shown
    const items = all.filter(
        (i) => (filter.noise || !i.noise) && (filter.context !== false || !i.context)
    );
    const cats = Object.keys(CATEGORIES).filter((c) => items.some((i) => i.cat === c));
    const toggleCat = (c) => {
        const cur = filter.cats ? new Set(filter.cats) : new Set(cats);
        if (cur.has(c)) cur.delete(c);
        else cur.add(c);
        set({ cats: cur.size === cats.length ? null : cur });
    };
    const only = (c) => set({ cats: new Set([c]) });
    const types = [...new Set(items.map((i) => i.label))].sort();
    // grouped options have to come sorted by their group
    const gates = [...new Set(items.map((i) => i.gate).filter(Boolean))].sort(
        (a, b) => gateGroup(a).localeCompare(gateGroup(b)) || a.localeCompare(b)
    );
    const problems = count(items, (i) => i.sev !== 'info');

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {cats.map((c) => {
                    const on = !filter.cats || filter.cats.has(c);
                    return (
                        <Chip
                            key={c}
                            size="small"
                            label={`${CATEGORIES[c].label} · ${count(items, (i) => i.cat === c)}`}
                            onClick={() => toggleCat(c)}
                            onDoubleClick={() => only(c)}
                            title="Kattintás: be/ki · dupla kattintás: csak ez"
                            variant={on ? 'filled' : 'outlined'}
                            sx={{
                                bgcolor: on ? CATEGORIES[c].color : undefined,
                                color: on ? '#fff' : CATEGORIES[c].color,
                                borderColor: CATEGORIES[c].color,
                                '&:hover': {
                                    bgcolor: on ? CATEGORIES[c].color : undefined,
                                    opacity: 0.85,
                                },
                            }}
                        />
                    );
                })}
                {filter.cats && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label="Mind"
                        onClick={() => set({ cats: null })}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            color="error"
                            checked={filter.problemsOnly}
                            onChange={(e) => set({ problemsOnly: e.target.checked })}
                        />
                    }
                    label={`Csak hibák / figyelmeztetések (${problems})`}
                />
                {showContext && (
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={filter.context !== false}
                                onChange={(e) => set({ context: e.target.checked })}
                            />
                        }
                        label="Kapu események közben"
                    />
                )}
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={filter.noise}
                            onChange={(e) => set({ noise: e.target.checked })}
                        />
                    }
                    label="Zaj is"
                />
            </Stack>
            {pickers && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={types}
                        value={filter.types}
                        onChange={(_, v) => set({ types: v })}
                        renderInput={(params) => <TextField {...params} label="Esemény típus" />}
                        sx={{ flex: 2, minWidth: 260 }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={gates}
                        groupBy={gateGroup}
                        getOptionLabel={gateLabel}
                        value={filter.gates}
                        onChange={(_, v) => set({ gates: v })}
                        renderInput={(params) => <TextField {...params} label="Kapu / eszköz" />}
                        sx={{ flex: 1, minWidth: 220 }}
                    />
                </Stack>
            )}
        </Stack>
    );
}
